import { useState } from "react";
import axios from "axios";
import { API } from "../../config";
import { AlertCircle, Loader2, TrendingUp, Zap } from "lucide-react";

export const SiteSpeedTool = ({ t, language, userEmail }) => {
  const [url, setUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);

  const handleCheck = async () => {
    if (!url.trim() || isLoading) return;
    setIsLoading(true);
    setResults(null);
    try {
      const res = await axios.post(`${API}/site-speed`, {
        url: url.trim().startsWith('http') ? url.trim() : `https://${url.trim()}`,
        language,
        email: userEmail || ''
      });
      setResults(res.data);
    } catch (e) {
      console.error('Site speed error:', e);
    } finally {
      setIsLoading(false);
    }
  };

  const ratingColors = { good: 'text-emerald-400 bg-emerald-900/20 border-emerald-800', needs_improvement: 'text-orange-400 bg-orange-900/20 border-orange-800', poor: 'text-red-400 bg-red-900/20 border-red-800' };
  const scoreColor = (s) => s >= 90 ? 'text-green-600' : s >= 50 ? 'text-orange-500' : 'text-red-600';
  const scoreBg = (s) => s >= 90 ? 'bg-green-500' : s >= 50 ? 'bg-orange-400' : 'bg-purple-500';

  return (
    <div data-testid="site-speed-tool">
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <input
          type="text"
          data-testid="speed-url-input"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleCheck()}
          placeholder={t.speed_url_placeholder}
          className="flex-1 px-4 py-3 border-2 border-[var(--color-border)] focus:border-[#a855f7] focus:outline-none transition-colors"
        />
        <button
          data-testid="speed-check-btn"
          onClick={handleCheck}
          disabled={isLoading || !url.trim()}
          className="px-6 py-3 bg-[#a855f7] text-white font-semibold hover:bg-purple-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Zap className="w-5 h-5" />}
          {t.speed_check}
        </button>
      </div>

      {results && (
        <div className="space-y-6 animate-fade-in" data-testid="speed-results">
          {/* Score Cards */}
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: t.speed_performance, score: results.performance_score },
              { label: t.speed_mobile, score: results.mobile_score },
              { label: t.speed_desktop, score: results.desktop_score },
            ].map((card, i) => (
              <div key={i} className="border border-[var(--color-border)] bg-[var(--color-surface)] p-6 text-center" data-testid={`speed-score-${i}`}>
                <div className="relative w-20 h-20 mx-auto mb-3">
                  <svg className="w-20 h-20 -rotate-90" viewBox="0 0 80 80">
                    <circle cx="40" cy="40" r="34" stroke="#e5e7eb" strokeWidth="6" fill="none" />
                    <circle cx="40" cy="40" r="34" stroke={card.score >= 90 ? '#22c55e' : card.score >= 50 ? '#f97316' : '#ef4444'} strokeWidth="6" fill="none"
                      strokeDasharray={`${(card.score / 100) * 213.6} 213.6`} strokeLinecap="round" />
                  </svg>
                  <span className={`absolute inset-0 flex items-center justify-center text-xl font-mono font-black ${scoreColor(card.score)}`}>{card.score}</span>
                </div>
                <p className="text-sm font-semibold text-[var(--color-text-muted)]">{card.label}</p>
              </div>
            ))}
          </div>

          {/* Core Web Vitals */}
          <div className="border border-[var(--color-border)] bg-[var(--color-surface)] overflow-hidden">
            <div className="p-4 border-b border-[var(--color-border)] bg-[var(--color-bg)]">
              <h3 className="font-bold flex items-center gap-2 font-['Chivo']">
                <Zap className="w-5 h-5 text-[#a855f7]" />
                Core Web Vitals
              </h3>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-px bg-gray-200">
              {results.metrics.map((m, i) => (
                <div key={i} className={`p-4 bg-[var(--color-surface)] border-l-4 ${ratingColors[m.rating]?.replace('text-', 'border-').split(' ')[0] || 'border-[var(--color-border)]'}`} data-testid={`metric-${i}`}>
                  <p className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider">{m.name}</p>
                  <div className="flex items-end justify-between mt-2">
                    <span className="text-2xl font-mono font-bold text-white">{m.value}</span>
                    <span className={`px-2 py-0.5 text-xs font-bold rounded ${ratingColors[m.rating] || 'bg-[var(--color-surface-alt)]'}`}>
                      {m.score}/100
                    </span>
                  </div>
                  <div className="mt-2 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${scoreBg(m.score)}`} style={{ width: `${m.score}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Industry Comparison */}
          <div className="border border-[var(--color-border)] bg-[var(--color-surface)] p-6" data-testid="speed-comparison">
            <h4 className="font-bold text-sm font-['Chivo'] mb-4">{t.speed_comparison}</h4>
            <div className="grid grid-cols-4 gap-4 text-center">
              {[
                { label: t.speed_your_score, value: results.comparison.your_score, color: scoreColor(results.comparison.your_score) },
                { label: t.speed_avg, value: results.comparison.industry_average, color: 'text-[var(--color-text-muted)]' },
                { label: t.speed_top, value: results.comparison.top_performers, color: 'text-green-600' },
                { label: t.speed_percentile, value: `${results.comparison.percentile}%`, color: 'text-[#a855f7]' },
              ].map((item, i) => (
                <div key={i}>
                  <p className={`text-2xl font-mono font-black ${item.color}`}>{item.value}</p>
                  <p className="text-xs font-medium text-[var(--color-text-muted)] mt-1">{item.label}</p>
                </div>
              ))}
            </div>
            {/* Bar chart comparison */}
            <div className="mt-6 space-y-3">
              {[
                { label: t.speed_your_score, value: results.comparison.your_score, color: scoreBg(results.comparison.your_score) },
                { label: t.speed_avg, value: results.comparison.industry_average, color: 'bg-gray-400' },
                { label: t.speed_top, value: results.comparison.top_performers, color: 'bg-green-500' },
              ].map((bar, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="w-28 text-xs font-medium text-[var(--color-text-muted)] text-right">{bar.label}</span>
                  <div className="flex-1 h-4 bg-[var(--color-surface-alt)] rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${bar.color}`} style={{ width: `${bar.value}%` }} />
                  </div>
                  <span className="w-8 text-xs font-mono font-bold">{bar.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Opportunities + Diagnostics */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="border border-[var(--color-border)] bg-[var(--color-surface)]" data-testid="speed-opportunities">
              <div className="p-4 border-b border-[var(--color-border)] bg-[var(--color-bg)]">
                <h4 className="font-bold text-sm flex items-center gap-2 font-['Chivo']">
                  <TrendingUp className="w-4 h-4 text-green-600" />
                  {t.speed_opportunities}
                </h4>
              </div>
              <div className="p-4 space-y-2">
                {results.opportunities.map((o, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <Zap className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                    <span>{o}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="border border-[var(--color-border)] bg-[var(--color-surface)]" data-testid="speed-diagnostics">
              <div className="p-4 border-b border-[var(--color-border)] bg-[var(--color-bg)]">
                <h4 className="font-bold text-sm flex items-center gap-2 font-['Chivo']">
                  <AlertCircle className="w-4 h-4 text-[#a855f7]" />
                  {t.speed_diagnostics}
                </h4>
              </div>
              <div className="p-4 space-y-2">
                {results.diagnostics.map((d, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <span>{d}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};



// Competitor Comparison Component  
